/*
 * Copyright (c) 2024. Bolmate B.V.
 */
import './style/searchVolumePage.scss';

import {AwesomeIcon, ChangeFunction, Checkbox, Loader, remToPx, SelectItemShape} from '@dvrd/dvr-controls';
import React, {KeyboardEventHandler, MouseEventHandler, useEffect, useMemo} from 'react';
import {BolSearchTerm, SearchVolumeData, SearchVolumePeriod, SearchVolumePeriodType} from "../../util/interfaces";
import {BolmateButton, BolmateInput, BolmateSelect} from "../controls";
import {parseThousandSeparator} from "../../util/utils";
import SearchVolumeBarChart from "./searchVolumeBarChart";
import SearchVolumeHeatChart from "./searchVolumeHeatChart";
import SearchVolumePieChart from "./searchVolumePieChart";
import SearchVolumeTreeChart from "./searchVolumeTreeChart";
import SearchVolumeLineChart from "./searchVolumeLineChart";
import classNames from 'classnames';
import ArrowDown from "../misc/arrowDown";
import ArrowUp from "../misc/arrowUp";
import BolmatePlaceholderOverlay from "../misc/bolmatePlaceholderOverlay";
import Image from "../misc/image";
import {DotsLoader} from "../controls/loader/dotsLoader";
import InfoBanner from "../misc/infoBanner";

interface Props {
    onChangeSearchTerm: ChangeFunction<string>;
    onSearch: MouseEventHandler & KeyboardEventHandler;
    onClickRelated: (term: string) => MouseEventHandler;
    onChangePeriod: ChangeFunction<SearchVolumePeriodType>;
    onRemoveTerm: (term: string | null) => MouseEventHandler;
    onChangeCombineTerms: ChangeFunction<boolean>;
    onChangeAISearchTerm: ChangeFunction<string>;
    searchTerm: string;
    searchTerms: Array<string>;
    searchVolumes: Array<SearchVolumeData>;
    comparingVolumes: Array<SearchVolumeData>;
    historicVolumes: Array<SearchVolumeData>;
    relatedTerms: Array<BolSearchTerm>;
    periodType: SearchVolumePeriodType;
    loading: boolean;
    loadingHistoric: boolean;
    combineTerms: boolean;
    aiSearchTerm: string;
    loadingAI: boolean;
    aiAnalysis: string;
}

const periodItems: Array<SelectItemShape> = [
    {label: 'Laatste week', value: SearchVolumePeriodType.LAST_WEEK},
    {label: 'Laatste 14 dagen', value: SearchVolumePeriodType.LAST_14_DAYS},
    {label: 'Laatste maand', value: SearchVolumePeriodType.LAST_MONTH},
    {label: 'Laatste 3 maanden', value: SearchVolumePeriodType.LAST_3_MONTHS},
    {label: 'Laatste 6 maanden', value: SearchVolumePeriodType.LAST_6_MONTHS},
    {label: 'Laatste jaar', value: SearchVolumePeriodType.LAST_YEAR},
    {label: 'Laatste 2 jaar', value: SearchVolumePeriodType.LAST_2_YEARS},
];

const chartsHeight = remToPx(23.5);
//bolt-lightning

export default function SearchVolumePage(props: Props) {
    const {
        searchVolumes, historicVolumes, loadingHistoric, onSearch, loading, searchTerms, onChangeSearchTerm, searchTerm,
        relatedTerms, onClickRelated, periodType, onChangePeriod, onRemoveTerm, comparingVolumes, combineTerms,
        onChangeCombineTerms, onChangeAISearchTerm, aiSearchTerm, loadingAI, aiAnalysis
    } = props;
    const comparingTotalValue = useMemo(() => {
        let totalVolume = 0;
        for (const searchVolume of comparingVolumes) {
            totalVolume += searchVolume.periods.reduce(
                (current: number, period: SearchVolumePeriod) => current + period.total, 0);
        }
        return totalVolume;
    }, [comparingVolumes]);
    const [averageVolume, totalVolume, totalDiffPercentage] = useMemo(() => {
        let averageVolume = 0;
        let totalVolume = 0;
        for (const searchVolume of searchVolumes) {
            const total = searchVolume.periods.reduce(
                (current: number, period: SearchVolumePeriod) => current + period.total, 0);
            totalVolume += total;
            averageVolume += Math.floor(total / searchVolume.periods.length);
        }
        let totalDiffPercentage = 0;
        if (totalVolume === 0 && comparingTotalValue > 0) totalDiffPercentage = -100;
        else if (totalVolume > 0 && comparingTotalValue === 0) totalDiffPercentage = 0;
        else if (totalVolume > 0 && comparingTotalValue > 0)
            totalDiffPercentage = Math.floor((totalVolume - comparingTotalValue) / comparingTotalValue * 100);
        return [averageVolume, totalVolume, totalDiffPercentage];
    }, [searchVolumes, comparingTotalValue]);
    const periodLabel = useMemo(() => {
        if (periodType === SearchVolumePeriodType.LAST_WEEK) return '7 dagen';
        else if (periodType === SearchVolumePeriodType.LAST_14_DAYS) return '14 dagen';
        else if (periodType === SearchVolumePeriodType.LAST_MONTH) return '31 dagen';
        else if (periodType === SearchVolumePeriodType.LAST_3_MONTHS) return '3 maanden';
        else if (periodType === SearchVolumePeriodType.LAST_6_MONTHS) return '6 maanden';
        else if (periodType === SearchVolumePeriodType.LAST_YEAR) return '12 maanden';
        else if (periodType === SearchVolumePeriodType.LAST_2_YEARS) return '24 maanden';
    }, [periodType]);
    const aiItems = useMemo(() => {
        const items: Array<SelectItemShape> = [{label: 'Kies een zoekterm', value: '', selectable: false}];
        return items.concat(searchTerms.map((term: string) => ({value: term, label: term})));
    }, [searchTerms]);
    const combinedLabel = useMemo(() => searchTerms.length > 1 ? ' - Opgeteld' : '', [searchTerms]);

    function onClickAIAnalyse() {
        if (!searchTerms.length) return;
        onChangeAISearchTerm(searchTerms[0]);
    }

    function renderSearch() {
        const hasSearchTerms = searchTerms.length > 0;
        return (
            <div className='search-container block'>
                <div className='block-header'>
                    <label className='block-title'>Vind zoekvolume en gerelateerde zoekwoorden</label>
                </div>
                <div className={classNames('block-content', hasSearchTerms && 'with-terms')} id='search-block'>
                    <Loader active={loading}/>
                    <div className='search-container'>
                        <BolmateInput onChange={onChangeSearchTerm} value={searchTerm} label='Zoekwoord'
                                      onEnter={onSearch} placeholder='Voer hier een zoekwoord in'
                                      id='search-volume-term-field' className='search-field'
                                      ornaments={{element: <AwesomeIcon name='search'/>}} autoFocus/>
                        <Checkbox className='combine-check' checked={combineTerms} onCheck={onChangeCombineTerms}
                                  label='Meerdere zoekwoorden combineren'/>
                    </div>
                    <div className='terms-container'>
                        {searchTerms.map(renderTerm)}
                        {searchTerms.length > 2 && (
                            <div className='search-term all' onClick={onRemoveTerm(null)}>
                                <label>Alles verwijderen</label>
                                <AwesomeIcon name='delete-left'/>
                            </div>
                        )}
                    </div>
                    <div className='period-row'>
                        <BolmateSelect onChange={onChangePeriod} value={periodType} items={periodItems}
                                       label='Periode'/>
                        <BolmateButton onClick={onSearch} label='Zoeken'/>
                    </div>
                </div>
            </div>
        )
    }

    function renderRelatedTerms() {
        return (
            <div className='related-terms block'>
                <div className='block-header'>
                    <label className='block-title'>Gerelateerde zoekwoorden</label>
                </div>
                <div className='block-content' id='related-terms-block'>
                    {!searchVolumes.length && <BolmatePlaceholderOverlay/>}
                    <div className='related-terms-list'>
                        {relatedTerms.map(renderRelatedTerm)}
                    </div>
                </div>
            </div>
        )
    }

    function renderRelatedTerm(term: BolSearchTerm, index: number) {
        return (
            <div key={index} className='related-search-term' onClick={onClickRelated(term.search_term)}>
                <label className='term'>{term.search_term}</label>
                <label className='total'>{parseThousandSeparator(term.total, ',')}</label>
            </div>
        )
    }

    function renderTerm(term: string, index: number) {
        return (
            <div key={index} className='search-term' onClick={onRemoveTerm(term)}>
                <label>{term}</label>
                <AwesomeIcon name='circle-xmark' prefix='far'/>
            </div>
        )
    }

    function renderStats() {
        const negativeComparison = totalDiffPercentage < 0;
        const comparisonIcon = negativeComparison ? <ArrowDown/> : <ArrowUp/>;
        const comparisonLabel = `${negativeComparison ? '-' : '+'}${Math.abs(totalDiffPercentage)} %`;
        return (
            <div className='stats-row page-row'>
                <div className='stat-container block'>
                    <div className='block-header'>
                        <label className='block-title'>Gemiddeld zoekvolume per maand{combinedLabel}</label>
                    </div>
                    <div className='block-content'>
                        <label className='stats-value'>{parseThousandSeparator(averageVolume, ',')}</label>
                    </div>
                </div>
                <div className='stat-container block'>
                    <div className='block-header'>
                        <label className='block-title'>Totaal zoekvolume ({periodLabel}){combinedLabel}</label>
                    </div>
                    <div className='block-content col-2'>
                        <label className='stats-value'>{parseThousandSeparator(totalVolume, ',')}</label>
                        <label className='comparison-value'>
                            {comparisonIcon}&nbsp;
                            <span>{comparisonLabel}</span>
                        </label>
                    </div>
                </div>
                <div className='stat-container block'>
                    <div className='block-header'>
                        <label className='block-title'>Totaal zoekvolume
                            (voorgaande {periodLabel}){combinedLabel}</label>
                    </div>
                    <div className='block-content'>
                        <label className='stats-value'>{parseThousandSeparator(comparingTotalValue, ',')}</label>
                    </div>
                </div>
            </div>
        )
    }

    function renderAI() {
        const actionsDisabled = loading || loadingAI;
        return (
            <div className='block'>
                <div
                    className={classNames('block-header ai-analysis-header',
                        searchVolumes.length > 0 && 'with-action')}>
                    <label className='block-title'>Markttrendanalyse met BolmateAI</label>
                    <div className='ai-action'>
                        {searchTerms.length > 1 &&
                            <BolmateSelect onChange={onChangeAISearchTerm} value={aiSearchTerm} items={aiItems}
                                           disabled={actionsDisabled}/>
                        }
                    </div>
                </div>
                <div className='block-content ai-content'>
                    {loadingAI && <div className='ai-loading-container'><DotsLoader dotSize='3rem'/></div>}
                    {(!searchVolumes.length || (!aiAnalysis.length && searchTerms.length > 1)) ?
                        (!aiAnalysis.length ? <BolmatePlaceholderOverlay/> : null) :
                        (!aiAnalysis.length && searchTerms.length <= 1) && <div className='button-container'>
                            <BolmateButton onClick={onClickAIAnalyse} label='Analyseren met BolmateAI'
                                           ornaments={{
                                               element: <Image name='logo-no-text' alt='' style={{
                                                   maxWidth: '1.2rem', maxHeight: '1.2rem', objectFit: 'scale-down'
                                               }}/>
                                           }} secondary/>
                        </div>
                    }
                    <p className='ai-analysis'>{aiAnalysis}</p>
                </div>
            </div>
        )
    }

    function renderBarChart() {
        return (
            <div className='block'>
                <div className='block-header'>
                    <label className='block-title'>Zoekvolume</label>
                </div>
                <div className='block-content'>
                    {!searchVolumes.length && <BolmatePlaceholderOverlay/>}
                    <SearchVolumeBarChart searchVolumes={searchVolumes} height={chartsHeight}/>
                </div>
            </div>
        );
    }

    function renderTreeMap() {
        return (
            <div className='block'>
                <div className='block-header'>
                    <label className='block-title'>Gerelateerde zoekwoorden</label>
                </div>
                <div className='block-content'>
                    {!searchVolumes.length && <BolmatePlaceholderOverlay/>}
                    <SearchVolumeTreeChart onClickRelated={onClickRelated} relatedTerms={relatedTerms}
                                           height={chartsHeight}/>
                </div>
            </div>
        )
    }

    function renderPieChart() {
        return (
            <div className='block pie'>
                <div className='block-header'>
                    <label className='block-title'>Nederland vs België totaal{combinedLabel}</label>
                </div>
                <div className='block-content'>
                    {!searchVolumes.length && <BolmatePlaceholderOverlay/>}
                    <SearchVolumePieChart searchVolumes={searchVolumes} height={chartsHeight}/>
                </div>
            </div>
        )
    }

    function renderHeatMap() {
        return (
            <div className='block'>
                <div className='block-header'>
                    <label className='block-title'>Nederland vs België per periode{combinedLabel}</label>
                </div>
                <div className='block-content'>
                    {!searchVolumes.length && <BolmatePlaceholderOverlay/>}
                    <SearchVolumeHeatChart searchVolumes={searchVolumes} height={chartsHeight}/>
                </div>
            </div>
        )
    }

    function renderLineChart() {
        return (
            <div className='block'>
                <div className='block-header'>
                    <label className='block-title'>Seizoensgebonden (4 jaar historische data)</label>
                </div>
                <div className='block-content'>
                    <Loader active={loadingHistoric}/>
                    {!historicVolumes.length && <BolmatePlaceholderOverlay/>}
                    <SearchVolumeLineChart searchVolumes={historicVolumes} height={chartsHeight}/>
                </div>
            </div>
        )
    }

    useEffect(() => {
        window.setTimeout(() => {
            const searchContainer = document.getElementById('search-block');
            const relatedTermsContainer = document.getElementById('related-terms-block');
            if (!searchContainer || !relatedTermsContainer) return;
            relatedTermsContainer.style.height = searchContainer.getBoundingClientRect().height + 'px';
        }, 200);
    }, [searchTerms]);

    return (
        <>
            <InfoBanner featureKey='search_volume' variant='info'
                        title='Zoekvolume'
                        message='Bekijk de zoekvolumes op bol. Vergelijk termen om kansen te vinden.'
                        showForNewUsersOnly={true}/>
            <div className='search-volume-page'>
                <div className='search-row page-row'>
                    {renderSearch()}
                    {renderRelatedTerms()}
                </div>
                {renderStats()}
                <div className='page-row ai-bar-row'>
                    {renderAI()}
                    {renderBarChart()}
                </div>
                <div className='charts-row page-row'>
                    {renderTreeMap()}
                    {renderPieChart()}
                </div>
                {renderHeatMap()}
                {renderLineChart()}
            </div>
        </>
    )
}
